import { API, BACKEND_URL } from '@/config/api';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Download, Loader2, Trash2, FileText, Calendar, 
  HardDrive, Clock, CheckCircle, AlertCircle 
} from 'lucide-react';

/**
 * SatelliteFTPDownload - Component for downloading satellite data via FTP
 * Allows users to download chlorophyll data for offline analysis
 */
export default function SatelliteFTPDownload() {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [downloads, setDownloads] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    dataset_id: 'cmems_obs-oc_glo_bgc-plankton_nrt_l4-gapfree-multi-4km_P1D',
    variable: 'CHL',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    min_lat: 57.5,
    max_lat: 71.5,
    min_lon: 3.0,
    max_lon: 31.0
  });

  // Fetch available datasets and download history on mount
  useEffect(() => {
    fetchDatasets();
    fetchDownloadHistory();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await axios.get(`${API}/satellite/ftp/available-datasets`, {
        withCredentials: true
      });
      if (response.data.success) {
        setDatasets(response.data.datasets);
      }
    } catch (error) {
      console.error('Error fetching datasets:', error);
    }
  };

  const fetchDownloadHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/satellite/ftp/downloads`, {
        withCredentials: true
      });
      if (response.data.success) {
        setDownloads(response.data.downloads);
      }
    } catch (error) {
      console.error('Error fetching download history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.post(`${API}/satellite/ftp/download`, formData, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success('Nedlasting fullført!', {
          description: `${response.data.total_size_mb} MB lastet ned`
        });
        fetchDownloadHistory();
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Nedlasting feilet', {
        description: error.response?.data?.detail || 'En feil oppstod'
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteDownload = async (downloadId) => {
    try {
      await axios.delete(`${API}/satellite/ftp/download/${downloadId}`, {
        withCredentials: true
      });
      toast.success('Nedlasting slettet');
      fetchDownloadHistory();
    } catch (error) {
      console.error('Error deleting download:', error);
      toast.error('Kunne ikke slette nedlastingen');
    }
  };

  const handleGetFile = async (downloadId, filename) => {
    try {
      window.open(`${API}/satellite/ftp/download/${downloadId}`, '_blank');
    } catch (error) {
      console.error('Error getting file:', error);
      toast.error('Kunne ikke hente filen');
    }
  };

  const selectedDataset = datasets.find(d => d.id === formData.dataset_id);

  return (
    <div className="space-y-6">
      {/* Download Form */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            Last ned satellittdata
          </CardTitle>
          <CardDescription>
            Last ned klorofylldata fra Copernicus Marine for offline analyse
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Dataset Selection */}
          <div className="space-y-2">
            <Label>Datasett</Label>
            <Select 
              value={formData.dataset_id} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, dataset_id: value }))}
            >
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Velg datasett" />
              </SelectTrigger>
              <SelectContent>
                {datasets.map((ds) => (
                  <SelectItem key={ds.id} value={ds.id}>
                    <div className="py-1">
                      <div className="font-semibold">{ds.name}</div>
                      <div className="text-xs text-slate-500">{ds.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDataset && (
              <div className="flex gap-2 mt-2">
                <Badge variant="outline">{selectedDataset.resolution}</Badge>
                <Badge variant="outline">{selectedDataset.temporal}</Badge>
                <Badge variant="outline">{selectedDataset.coverage}</Badge>
              </div>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Fra dato</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="pl-10 h-12"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">Til dato</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="pl-10 h-12"
                />
              </div>
            </div>
          </div>

          {/* Geographic Bounds */}
          <div className="space-y-3">
            <Label>Geografisk område (Norge = standard)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <span className="text-xs text-slate-500">Min breddegrad</span>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.min_lat}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_lat: parseFloat(e.target.value) }))}
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-slate-500">Max breddegrad</span>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.max_lat}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_lat: parseFloat(e.target.value) }))}
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-slate-500">Min lengdegrad</span>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.min_lon}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_lon: parseFloat(e.target.value) }))}
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-slate-500">Max lengdegrad</span>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.max_lon}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_lon: parseFloat(e.target.value) }))}
                  className="h-10"
                />
              </div>
            </div>
          </div>

          {/* Download Button */}
          <Button 
            onClick={handleDownload}
            disabled={downloading}
            className="w-full h-12 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
          >
            {downloading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Laster ned data...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Last ned data
              </>
            )}
          </Button>
          
          <p className="text-xs text-slate-500 text-center">
            Nedlasting kan ta opptil 5 minutter avhengig av datamengde
          </p>
        </CardContent>
      </Card>

      {/* Download History */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-slate-600" />
            Nedlastingshistorikk
          </CardTitle>
          <CardDescription>
            Dine tidligere nedlastinger (tilgjengelig i 24 timer)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : downloads.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Ingen nedlastinger ennå</p>
            </div>
          ) : (
            <div className="space-y-3">
              {downloads.map((dl) => (
                <div 
                  key={dl.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      dl.status === 'completed' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-amber-100 text-amber-600'
                    }`}>
                      {dl.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <AlertCircle className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">
                        {dl.variable} - {dl.date_range}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {new Date(dl.created_at).toLocaleString('nb-NO')}
                        <span className="text-slate-300">•</span>
                        <HardDrive className="w-3 h-3" />
                        {dl.total_size_mb} MB
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {dl.status === 'completed' && dl.files?.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGetFile(dl.id, dl.files[0].filename)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Last ned
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteDownload(dl.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
